import { api } from '../api'
import { META_KEYS, metaSet } from '../db/db'
import type { LocalSession } from '../db/types'

/**
 * §11.
 *
 * Every elapsed and remaining time in the app derives from timestamps. Nothing
 * accumulates ticks, because browsers throttle `setInterval` in background tabs
 * to a few firings per minute and a phone puts the tab in the background
 * constantly. A `setInterval` in this app drives repaint and never truth.
 */

let clockOffsetMs = 0

/** Kept current from `X-Server-Time` on every response, not only at session start. */
api.onServerTime(({ serverTimeMs, receivedAtMs }) => {
  clockOffsetMs = serverTimeMs - receivedAtMs
  void metaSet(META_KEYS.clockOffsetMs, clockOffsetMs)
})

export function getClockOffsetMs(): number {
  return clockOffsetMs
}

/** `Date.now()` translated onto the server clock. */
export function serverNow(): number {
  return Date.now() + clockOffsetMs
}

export interface Remaining {
  /** Milliseconds left; negative once the deadline has passed. `null` for an untimed drill. */
  remainingMs: number | null
  expired: boolean
}

export function remainingFor(session: Pick<LocalSession, 'startedAtServer' | 'durationSec' | 'clockOffsetMs'>): Remaining {
  if (session.durationSec === null) return { remainingMs: null, expired: false }

  // The session's own offset is used in preference to the live one: it was
  // measured against the same server response that issued `startedAtServer`.
  const nowServer = Date.now() + session.clockOffsetMs
  const remainingMs = session.startedAtServer + session.durationSec * 1000 - nowServer
  return { remainingMs, expired: remainingMs <= 0 }
}

export function formatRemaining(remainingMs: number): string {
  const clamped = Math.max(0, remainingMs)
  const totalSec = Math.floor(clamped / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}:${String(sec).padStart(2, '0')}`
}
